import { forwardRef, type CSSProperties, type ReactNode } from "react";
import { InboxOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Spin } from "./Spin";
import { cx } from "./fieldContext";
import { useUiText } from "./uiText";
const MAX_PAGES_WITHOUT_GAP = 7;

export interface ItemListPaginationConfig {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  /** Antd-kompatibel; die Liste hat keine Größenwahl. */
  showSizeChanger?: boolean;
}

export interface ItemListProps<T> {
  dataSource?: readonly T[];
  renderItem: (item: T, index: number) => ReactNode;
  loading?: boolean;
  /** Antd-kompatibel: Text des leeren Zustands. */
  locale?: { emptyText?: ReactNode };
  pagination?: ItemListPaginationConfig | false;
  /** `divided`: Zeilen mit Trennlinien (Standard); `cards`: jede Zeile als eigene Karte. */
  variant?: "divided" | "cards";
  className?: string;
  style?: CSSProperties;
  /** Antd-kompatibel; die Liste hat eine Zeilengröße. */
  size?: "small" | "default" | "large";
}

/** Seitenwahl mit höchstens sieben Knöpfen: erste, letzte und die Nachbarn der aktuellen Seite. */
function pageItems(current: number, count: number): (number | "gap")[] {
  if (count <= MAX_PAGES_WITHOUT_GAP) return Array.from({ length: count }, (_, i) => i + 1);
  const pages = new Set([1, count, current - 1, current, current + 1]);
  const sorted = [...pages].filter((page) => page >= 1 && page <= count).sort((a, b) => a - b);
  const items: (number | "gap")[] = [];
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) items.push("gap");
    items.push(page);
  });
  return items;
}

function ItemListPagination({ current, pageSize, total, onChange }: ItemListPaginationConfig) {
  const { text } = useUiText();
  const count = Math.max(1, Math.ceil(total / pageSize));
  if (count <= 1) return null;
  return (
    <nav className="ck-pagination" aria-label={text("Pages")}>
      <button type="button" className="ck-page-btn" aria-label={text("Previous page")} disabled={current <= 1} onClick={() => onChange(current - 1)}>
        <LeftOutlined aria-hidden />
      </button>
      {pageItems(current, count).map((item, index) =>
        item === "gap" ? (
          <span key={`gap-${index}`} className="ck-page-gap" aria-hidden="true">…</span>
        ) : (
          <button
            key={item}
            type="button"
            className="ck-page-btn"
            aria-current={item === current ? "page" : undefined}
            aria-label={text("Page {page}").replace("{page}", String(item))}
            onClick={() => onChange(item)}
          >
            {item}
          </button>
        ),
      )}
      <button type="button" className="ck-page-btn" aria-label={text("Next page")} disabled={current >= count} onClick={() => onChange(current + 1)}>
        <RightOutlined aria-hidden />
      </button>
    </nav>
  );
}

/**
 * Zeilenliste des Kits (aus my-vitalteam übernommen 14.10.2026, Aufrufform wie Antd `List`): Zeilen,
 * Ladezustand über dem Inhalt, einheitlicher leerer Zustand und Seitenwahl darunter. Die Antd-`List`
 * aus dem Kit bleibt für bestehende Aufrufer unverändert.
 */
export function ItemListRoot<T>({ dataSource = [], renderItem, loading = false, locale, pagination, variant = "divided", className, style }: ItemListProps<T>) {
  const empty = dataSource.length === 0;
  return (
    <div className={cx("ck-item-list", variant === "cards" && "ck-item-list--cards", className)} style={style} aria-busy={loading || undefined}>
      {empty && !loading ? (
        <div className="ck-item-list-empty">
          <span className="ck-item-list-empty-icon" aria-hidden="true">
            <InboxOutlined />
          </span>
          {locale?.emptyText}
        </div>
      ) : (
        <ul className="ck-item-list-items">{dataSource.map((item, index) => renderItem(item, index))}</ul>
      )}
      {loading ? (
        <div className="ck-item-list-loading" data-empty={empty || undefined}>
          <Spin />
        </div>
      ) : null}
      {pagination ? <ItemListPagination {...pagination} /> : null}
    </div>
  );
}

export interface ItemListItemProps {
  className?: string;
  /** Ganze Zeile anklickbar (zusätzlich zur Aktion in der Zeile, die per Tastatur erreichbar bleibt). */
  onClick?: () => void;
  style?: CSSProperties;
  children?: ReactNode;
}

export const ItemListItem = forwardRef<HTMLLIElement, ItemListItemProps>(function ItemListItem({ className, style, onClick, children }, ref) {
  return (
    <li ref={ref} className={cx("ck-item-list-item", className)} style={style} onClick={onClick}>
      {children}
    </li>
  );
});
